import { useState, useEffect, useCallback } from 'react';
import { hardReloadApp, stripAppUpdateQueryFromUrl } from '@utils/appHardReload';

const STORAGE_KEY = 'code360_app_build_id';
const VERSION_URL = '/version.json';

/**
 * Detects a new production deploy by comparing `/version.json` (written at each `vite build`)
 * with the last acknowledged build id in localStorage. Shows UI when a new build is on the server.
 */
export function useAppDeployUpdate() {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [remoteBuildId, setRemoteBuildId] = useState(null);
    const [isApplying, setIsApplying] = useState(false);

    const check = useCallback(async () => {
        if (import.meta.env.DEV) return;
        try {
            const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
                cache: 'no-store',
                credentials: 'same-origin',
            });
            if (!res.ok) return;
            const data = await res.json();
            const id = data?.buildId ?? data?.version;
            if (id == null || id === '') return;

            setRemoteBuildId(id);
            const local = localStorage.getItem(STORAGE_KEY);
            if (!local) {
                localStorage.setItem(STORAGE_KEY, id);
                return;
            }
            if (local !== id) {
                setUpdateAvailable(true);
            }
        } catch {
            /* offline or bad JSON */
        }
    }, []);

    useEffect(() => {
        stripAppUpdateQueryFromUrl();
    }, []);

    useEffect(() => {
        if (import.meta.env.DEV) return undefined;
        check();
        const interval = setInterval(check, 5 * 60 * 1000);
        const onFocus = () => check();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [check]);

    const applyUpdate = useCallback(async () => {
        if (isApplying) return;
        setIsApplying(true);
        try {
            await hardReloadApp({
                acknowledgedBuildId: remoteBuildId,
                buildStorageKey: STORAGE_KEY,
            });
        } catch {
            setIsApplying(false);
        }
    }, [remoteBuildId, isApplying]);

    return { updateAvailable, applyUpdate, isApplying, checkNow: check };
}
